import { useState, useMemo } from 'react';
import {
  FileText,
  Search,
  Filter,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Building,
  Mail,
  User,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/Table.jsx';
import { formatCurrency, formatDate } from '../../lib/utils.js';
import { INQUIRY_STATUS_MAP } from './admin-presets.js';
import { InquiryDetailDrawer } from './InquiryDetailDrawer.jsx';

export function InquiriesQuotesTab({
  inquiries = [],
  tenants = [],
  onUpdateInquiryStatus,
  updating = false,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenantFilter, setSelectedTenantFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = { all: inquiries.length, new: 0, in_review: 0, quoted: 0, accepted: 0, rejected: 0, archived: 0 };
    for (const inq of inquiries) {
      const st = inq.status || 'new';
      if (counts[st] !== undefined) counts[st]++;
    }
    return counts;
  }, [inquiries]);

  // Filtered inquiries
  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inq) => {
      const matchTenant =
        selectedTenantFilter === 'all' ||
        inq.tenantId === selectedTenantFilter ||
        inq.tenantSlug === selectedTenantFilter;

      const matchStatus =
        selectedStatusFilter === 'all' || (inq.status || 'new') === selectedStatusFilter;

      const q = searchQuery.toLowerCase().trim();
      const matchSearch =
        !q ||
        inq.inquiryNumber?.toLowerCase().includes(q) ||
        inq.id?.toLowerCase().includes(q) ||
        inq.customerName?.toLowerCase().includes(q) ||
        inq.customerEmail?.toLowerCase().includes(q) ||
        inq.customerCompany?.toLowerCase().includes(q) ||
        inq.tenantName?.toLowerCase().includes(q);

      return matchTenant && matchStatus && matchSearch;
    });
  }, [inquiries, selectedTenantFilter, selectedStatusFilter, searchQuery]);

  const handleOpenDetail = (inquiry) => {
    setSelectedInquiry(inquiry);
  };

  const handleUpdateStatus = async (inquiryId, newStatus, notes) => {
    if (onUpdateInquiryStatus) {
      await onUpdateInquiryStatus(inquiryId, newStatus, notes);
    }
    // Update local drawer state if open
    setSelectedInquiry((prev) => (prev && prev.id === inquiryId ? { ...prev, status: newStatus, notes } : prev));
  };

  const statusFilterTabs = [
    { key: 'all', label: 'Alle', count: statusCounts.all },
    { key: 'new', label: 'Neu', count: statusCounts.new },
    { key: 'in_review', label: 'In Prüfung', count: statusCounts.in_review },
    { key: 'quoted', label: 'Angebot erstellt', count: statusCounts.quoted },
    { key: 'accepted', label: 'Angenommen', count: statusCounts.accepted },
    { key: 'rejected', label: 'Abgelehnt', count: statusCounts.rejected },
    { key: 'archived', label: 'Archiviert', count: statusCounts.archived },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 border-b border-[var(--border)] pb-4">
        <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
          B2B-Anfragen & Angebots-Workflow
        </h2>
        <p className="text-xs text-[var(--secondary)]">
          Übersicht aller über die Partner-Shops eingegangenen Projektanfragen und Angebote mit Status-Triage.
        </p>
      </div>

      {/* Status Filter Tabs / Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
        {statusFilterTabs.map((tab) => {
          const isActive = selectedStatusFilter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSelectedStatusFilter(tab.key)}
              className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-[var(--primary)] text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                  isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-72">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Anfrage suchen (Nr., Kunde, Firma)..."
              className="pl-9 text-xs"
            />
          </div>

          <select
            value={selectedTenantFilter}
            onChange={(e) => setSelectedTenantFilter(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-white px-3 py-2 text-xs font-medium text-slate-700"
          >
            <option value="all">Alle Mandanten</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="text-xs text-slate-500 font-medium">
          {filteredInquiries.length} Anfragen gefunden
        </div>
      </div>

      {/* Inquiries Table */}
      <Card className="border-[var(--border)] bg-white shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="font-semibold text-xs">Anfrage-Nr. & Datum</TableHead>
                <TableHead className="font-semibold text-xs">Mandant</TableHead>
                <TableHead className="font-semibold text-xs">Kunde & Firma</TableHead>
                <TableHead className="font-semibold text-xs text-center">Positionen</TableHead>
                <TableHead className="font-semibold text-xs text-right">Geschätzter Netto-Wert</TableHead>
                <TableHead className="font-semibold text-xs text-center">Status</TableHead>
                <TableHead className="font-semibold text-xs text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-slate-500">
                    Keine Anfragen passend zu den Filtern gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                filteredInquiries.map((inq) => {
                  const statusConf = INQUIRY_STATUS_MAP[inq.status || 'new'] || INQUIRY_STATUS_MAP.new;
                  const totalCents = inq.totalEstimatedCents || inq.total_estimated || 0;
                  const formattedTotal = inq.totalEstimatedFormatted || formatCurrency(totalCents);

                  return (
                    <TableRow key={inq.id} className="hover:bg-slate-50/60 transition-colors">
                      {/* Inquiry Number & Date */}
                      <TableCell>
                        <div className="font-mono text-xs font-bold text-slate-900">
                          {inq.inquiryNumber || inq.id}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {formatDate(inq.createdAt || inq.created_at)}
                        </div>
                      </TableCell>

                      {/* Tenant */}
                      <TableCell>
                        <Badge variant="outline" className="text-[11px] font-medium">
                          {inq.tenantName || inq.tenantSlug || inq.tenantId}
                        </Badge>
                      </TableCell>

                      {/* Customer */}
                      <TableCell>
                        <div className="text-xs font-semibold text-slate-900">
                          {inq.customerName || '-'}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {inq.customerCompany ? `${inq.customerCompany} • ` : ''}{inq.customerEmail}
                        </div>
                      </TableCell>

                      {/* Item Count */}
                      <TableCell className="text-center">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                          {inq.itemCount || inq.items?.length || 1}
                        </span>
                      </TableCell>

                      {/* Estimated Total Net */}
                      <TableCell className="text-right font-mono text-xs font-bold text-slate-900">
                        {formattedTotal}
                      </TableCell>

                      {/* Status */}
                      <TableCell className="text-center">
                        <Badge variant={statusConf.badgeVariant} className="text-[10px]">
                          {statusConf.label}
                        </Badge>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDetail(inq)}
                          className="h-7 text-xs px-2.5 gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Inquiry Detail Drawer */}
      <InquiryDetailDrawer
        isOpen={Boolean(selectedInquiry)}
        onClose={() => setSelectedInquiry(null)}
        inquiry={selectedInquiry}
        onUpdateStatus={handleUpdateStatus}
        updating={updating}
      />
    </div>
  );
}
